import { Disclosure, Heading } from 'react-aria-components';
import ChevronDown from '@/assets/icons/ChevronDown.svg';
import { Button } from '@/components/Button';
import { ButtonVariant } from '@/constants';
import type { GiftCard } from '@/types';
import * as Styled from './GiftCards.styled';
import { useApplyGiftCard } from './useApplyGiftCard';
import { useCallback } from 'react';
import { useGiftCards } from './useGiftCards';

interface GiftCardDisclosureProps {
  cardData: GiftCard;
  ordinal: number;
}

export const StoredCard = ({
  cardData: { applied, cardNumber, isApplied, remaining },
  ordinal,
}: GiftCardDisclosureProps) => {
  const { applyGiftCard, isPending } = useApplyGiftCard(isApplied);
  const { getMaskedCardNumber } = useGiftCards();

  const handleClick = useCallback(() => {
    if (!isPending) {
      applyGiftCard({ giftCardNumber: cardNumber });
    }
  }, [applyGiftCard, cardNumber, isPending]);

  return (
    <Disclosure id={cardNumber} key={cardNumber}>
      <Heading level={3}>
        <Styled.Trigger slot="trigger">
          {ordinal}. Gift card &hellip;{cardNumber.slice(-4)}
          <ChevronDown />
        </Styled.Trigger>
      </Heading>
      <Styled.Panel>
        <Styled.Highlight>
          <dl>
            <div>
              <dt>Remaining balance</dt>
              <dd>{remaining}</dd>
            </div>
            <div>
              <dt>Card number</dt>
              <dd>{getMaskedCardNumber(cardNumber)}</dd>
            </div>
          </dl>
        </Styled.Highlight>
        {isApplied ? (
          <Styled.Text>
            You have applied <span>{applied}</span> to this order.
          </Styled.Text>
        ) : null}
        <Button
          handler={handleClick}
          isPending={isPending}
          variant={ButtonVariant.FullWidth}
        >
          {isApplied ? 'Remove' : 'Apply'}
        </Button>
      </Styled.Panel>
    </Disclosure>
  );
};
